import { getKeyForValueFromMap } from '../get-key-for-value-from-map/get-key-for-value-from-map'

const urlSegmentOrderPriorities = new Map([
  ['make', 1],
  ['model', 2],
  ['trim', 3],
  ['fuelType', 4],
  ['colour', 5],
  ['bodyType', 6],
  ['gearbox', 7],
  ['doors', 8],
  ['seats', 9],
  ['driveType', 10],
  ['vehicleYear', 11],
  ['rangeInMiles', 12],
])

const createReorderedParsedFilterMap = <TValue>(
  parsedFilterObject: Record<string, TValue>,
  orderedPrioritiesOfParsedFilterNames: number[],
) =>
  orderedPrioritiesOfParsedFilterNames.reduce(
    (reorderedParsedFilterMap, priority) => {
      // get filterName for priority
      const filterName = getKeyForValueFromMap(
        urlSegmentOrderPriorities,
        priority,
      )
      if (!filterName) {
        throw new Error(`There is no filterName with priority ${priority}`)
      }

      reorderedParsedFilterMap.set(filterName, parsedFilterObject[filterName])
      return reorderedParsedFilterMap
    },
    new Map<string, TValue>(),
  )

const isNumericArrayInAscendingOrder = (array: number[]) => {
  for (let i = 1; i < array.length; i++) {
    if (array[i] < array[i - 1]) {
      return false
    }
  }
  return true
}

const getPrioritiesOfParsedFilterNames = (filterNames: string[]) =>
  filterNames.map((filterName) => {
    const priority = urlSegmentOrderPriorities.get(filterName)
    if (!priority) throw new Error(`FilterName ${filterName} is unrecognised!`)
    return priority
  })

const getOrderedPrioritiesOfParsedFilterNames = (
  prioritiesOfParsedFilterNames: number[],
) => [...prioritiesOfParsedFilterNames].sort((a, b) => a - b)

/**
 * Checks if the URL path is in order. If it is, it returns the original input path
 * otherwise it will sort and return the sorted path.
 * It also returns if the path was sorted
 * @param parsedFilterMap The parsed URL path
 * @returns A validated, sorted URL path and a boolean to indicate if this function sorted the path
 */
export function getOrderedParsedFilterMap<TValue>(
  parsedFilterMap: Map<string, TValue>,
): {
  wasParsedFilterMapReordered: boolean
  orderedParsedFilterMap: Map<string, TValue>
} {
  const parsedFilterObject = Object.fromEntries(parsedFilterMap)
  const filterNames = Object.keys(parsedFilterObject)

  const prioritiesOfParsedFilterNames =
    getPrioritiesOfParsedFilterNames(filterNames)

  if (!isNumericArrayInAscendingOrder(prioritiesOfParsedFilterNames)) {
    const orderedPrioritiesOfParsedFilterNames =
      getOrderedPrioritiesOfParsedFilterNames(prioritiesOfParsedFilterNames)

    const orderedParsedFilterMap = createReorderedParsedFilterMap(
      parsedFilterObject,
      orderedPrioritiesOfParsedFilterNames,
    )

    return {
      wasParsedFilterMapReordered: true,
      orderedParsedFilterMap,
    }
  }

  return {
    wasParsedFilterMapReordered: false,
    orderedParsedFilterMap: parsedFilterMap,
  }
}
